/**
 * NexusCoin — Holographic 3D-like coin used across Créditos + PurchaseModal.
 *
 * Layers (from outer to inner):
 *   1. Soft radial cyan aura (25-35% cyan bleed).
 *   2. Outer ring — hair-thin bright cyan border with an inset cyan glow.
 *   3. Middle ring — dashed rotating 22s clockwise.
 *   4. Inner metallic disk — very dark radial with cyan rim glow.
 *   5. Central "N" glyph in monospace, cyan glow textShadow.
 *   6. Two tiny sparkle dots at the corners.
 *
 * Fully responsive via a single `size` prop (px). Defaults tuned for the
 * Créditos balance card (~150px) and the PurchaseModal (~130px).
 */
import React from 'react';

type Props = { size?: number; className?: string; testId?: string };

export const NexusCoin: React.FC<Props> = ({ size = 150, className = '', testId = 'nexus-coin' }) => (
  <div
    className={`relative shrink-0 ${className}`}
    style={{ width: size, height: size }}
    data-testid={testId}
    aria-hidden
  >
    {/* Outer aura */}
    <div
      className="absolute inset-0 rounded-full"
      style={{ background: 'radial-gradient(closest-side, hsl(var(--accent) / 0.35), transparent 70%)' }}
    />
    {/* Outer ring */}
    <div
      className="absolute inset-[6%] rounded-full"
      style={{
        border: '1px solid hsl(var(--accent) / 0.4)',
        boxShadow:
          '0 0 24px hsl(var(--accent) / 0.35) inset, ' +
          '0 0 32px hsl(var(--accent) / 0.25)',
      }}
    />
    {/* Middle dashed rotating ring */}
    <div
      className="absolute inset-[15%] rounded-full"
      style={{
        border: '1px dashed hsl(var(--accent) / 0.55)',
        animation: 'spin 22s linear infinite',
      }}
    />
    {/* Extra thin ring for depth */}
    <div
      className="absolute inset-[22%] rounded-full"
      style={{ border: '1px solid hsl(var(--accent) / 0.15)' }}
    />
    {/* Inner metallic coin */}
    <div
      className="absolute inset-[28%] rounded-full grid place-items-center"
      style={{
        background:
          'radial-gradient(circle at 32% 28%, rgba(20,35,45,1), rgba(3,6,9,1) 70%)',
        border: '1.4px solid hsl(var(--accent) / 0.65)',
        boxShadow:
          '0 0 26px hsl(var(--accent) / 0.5), ' +
          'inset 0 0 22px hsl(var(--accent) / 0.18)',
      }}
    >
      <span
        style={{
          color: 'hsl(var(--accent))',
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontWeight: 700,
          fontSize: `${Math.round(size * 0.30)}px`,
          lineHeight: 1,
          textShadow: '0 0 12px hsl(var(--accent) / 0.9), 0 0 3px hsl(var(--accent))',
        }}
      >N</span>
    </div>
    {/* Sparkles */}
    <div
      className="absolute rounded-full"
      style={{
        width: `${Math.max(3, size * 0.02)}px`,
        height: `${Math.max(3, size * 0.02)}px`,
        top: `${size * 0.10}px`,
        right: `${size * 0.14}px`,
        background: 'hsl(var(--accent))',
        boxShadow: '0 0 10px hsl(var(--accent))',
      }}
    />
    <div
      className="absolute rounded-full"
      style={{
        width: `${Math.max(2, size * 0.015)}px`,
        height: `${Math.max(2, size * 0.015)}px`,
        bottom: `${size * 0.18}px`,
        left: `${size * 0.10}px`,
        background: 'hsl(var(--accent) / 0.75)',
        boxShadow: '0 0 8px hsl(var(--accent) / 0.6)',
      }}
    />
  </div>
);

export default NexusCoin;
